import "./commodity-totals.css";

import { NumericTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { useContext, useMemo } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { imperialMetricConverter } from "~/features/quote/utils/imperial-metric-converter.ts";
import { ceilToDecimal } from "~/shared/utils/ceil-to-decimal.ts";

const formatQuantityValue = (value: number) => {
	if (value === 0) return "—";
	return value;
};

const formatDecimalValue = (value: number | string | null | undefined) => {
	if (value == null || value === "") return "—";
	const numericValue =
		typeof value === "string" ? Number.parseFloat(value) : value;
	if (!Number.isFinite(numericValue) || numericValue === 0) {
		return "—";
	}
	return numericValue.toFixed(2);
};

const summaryValueClass = (formatted: number | string) =>
	formatted === "—"
		? "item-summary-value item-summary-value-empty"
		: "item-summary-value";
export const CommodityTotals = () => {
	const { quoteState, onChange } = useContext(QuoteDetailsContext);
	const totals = useMemo(() => {
		const isMetric = quoteState.measuringType === "KG/CM";

		let qty = 0;
		let imperialWeight = 0;

		for (const item of quoteState.items) {
			qty += item.quantity ?? 0;

			if (isMetric) {
				// Convert from metric to imperial for consistent internal calculations
				const imperials = imperialMetricConverter("LB/IN", {
					weight: item.weight ?? 0,
					length: item.length ?? 0,
					width: item.width ?? 0,
					height: item.height ?? 0,
				});

				imperialWeight += ceilToDecimal(imperials.weight ?? 0, 2);
			} else {
				// imperialWeight += ceilToDecimal(
				// 	(item.weight ?? 0) * (item.quantity ?? 0),
				// 	2,
				// );
				imperialWeight += ceilToDecimal(item.weight ?? 0, 2);
			}
		}

		// Calculate conversions if in metric mode
		let metricWeight: number | null = null;
		let metricLinearFeet: number | null = null;

		if (isMetric) {
			const converted = imperialMetricConverter(
				"KG/CM",
				{
					weight: imperialWeight,
					length: 0,
					width: 0,
					height: 0,
				},
				quoteState.totalLinearFeet,
			);
			metricWeight = ceilToDecimal(converted.weight ?? 0, 2);
			metricLinearFeet = ceilToDecimal(converted.totalLinearFeet ?? 0, 2);
		}

		return {
			qty,
			weight: imperialWeight,
			convertedWeight: metricWeight,
			linearFeet: Number(quoteState.totalLinearFeet ?? 0),
			convertedLinearFeet: metricLinearFeet,
			isMetric,
		};
	}, [quoteState.items, quoteState.measuringType, quoteState.totalLinearFeet]);

	return (
		<div className={"item-summary-data"}>
			<div data-summary-field={"handling-unit"}>
				<div>Handling Unit</div>
				<div className={summaryValueClass(formatQuantityValue(totals.qty))}>
					{formatQuantityValue(totals.qty)}
				</div>
				{totals.isMetric ? (
					<div
						className={`text-red ${summaryValueClass(formatQuantityValue(totals.qty))}`}
					>
						{formatQuantityValue(totals.qty)}
					</div>
				) : null}
			</div>
			<div data-summary-field={"total-unit-weight"}>
				<div>Total Unit Weight</div>
				<div className={summaryValueClass(formatDecimalValue(totals.weight))}>
					{formatDecimalValue(totals.weight)}
				</div>
				{totals.convertedWeight ? (
					<div
						className={`text-red ${summaryValueClass(formatDecimalValue(totals.convertedWeight))}`}
					>
						{formatDecimalValue(totals.convertedWeight)}
					</div>
				) : null}
			</div>
			<div data-summary-field={"calculated-linear-feet"}>
				<div>Calculated Linear Feet</div>
				<div
					className={summaryValueClass(formatDecimalValue(totals.linearFeet))}
				>
					{formatDecimalValue(totals.linearFeet)}
				</div>
				{totals.convertedLinearFeet ? (
					<div
						className={`text-red ${summaryValueClass(formatDecimalValue(totals.convertedLinearFeet))}`}
					>
						{formatDecimalValue(totals.convertedLinearFeet)}
					</div>
				) : null}
			</div>
			<div data-summary-field={"overwrite-clf"}>
				<div>Overwrite Calculated Linear Feet</div>
				<div className={"linear-calculation"}>
					<NumericTextBox
						onChange={(event) =>
							onChange("customTotalLinearFeet", event.value ?? 0)
						}
						spinners={false}
						tabIndex={-1}
						value={quoteState.customTotalLinearFeet ?? 0}
					/>{" "}
					<span>Ft</span>
				</div>
			</div>
		</div>
	);
};
